import { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Text,
  Button,
  Box,
  HStack,
  Icon,
  List,
  ListItem,
  ListIcon,
  Heading,
} from "@chakra-ui/react";
import {
  CheckCircleIcon,
  ArrowUpIcon,
  AttachmentIcon,
  WarningIcon,
} from "@chakra-ui/icons";
import { FileUploader } from "react-drag-drop-files";
import { postUploadContacts } from "../../../api/clientUser/postUploadContacts";
import { useAuth } from "../../../hooks/useAuth";
import supabase from "../../../supabase";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientId?: string; // Optional: if provided, use this instead of getting from session
  accessToken?: string; // Optional: if provided, use this instead of getting from useAuth
}

const fileTypes = ["CSV"];

type ModalState = "upload" | "processing" | "error";

export const UploadContactsModal = ({ isOpen, onClose, onSuccess, clientId: providedClientId, accessToken: providedAccessToken }: Props) => {
  const { accessToken: authAccessToken } = useAuth();
  const accessToken = providedAccessToken || authAccessToken;
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [modalState, setModalState] = useState<ModalState>("upload");

  const handleFileChange = (file: File) => {
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !accessToken) return;

    setIsUploading(true);
    try {
      // Use provided clientId (from Admin) or get from supabase session
      let clientId = providedClientId;

      if (!clientId) {
        const { data: sessionData } = await supabase.auth.getSession();
        clientId = sessionData.session?.user?.id;
      }

      if (!clientId) {
        throw new Error("Unable to get client ID");
      }

      await postUploadContacts(clientId, selectedFile, accessToken);

      // Show processing success state
      setModalState("processing");
      setIsUploading(false);
    } catch (error: any) {
      console.error("Failed to upload contacts:", error);
      setModalState("error");
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setModalState("upload");
    onClose();
  };

  const handleDone = () => {
    setSelectedFile(null);
    setModalState("upload");
    onSuccess();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {modalState === "upload" && "Upload Contact List"}
          {modalState === "processing" && "Processing Contacts"}
          {modalState === "error" && "Upload Failed"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {/* Upload State */}
          {modalState === "upload" && (
            <VStack spacing={6} align="stretch">
              {/* Icon */}
              <Box display="flex" justifyContent="center" pt={2}>
                <Box
                  width="80px"
                  height="80px"
                  borderRadius="full"
                  bgGradient="linear(to-br, green.400, green.600)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 8px 20px rgba(72, 187, 120, 0.3)"
                >
                  <Icon as={AttachmentIcon} boxSize={10} color="white" />
                </Box>
              </Box>

              {/* Description */}
              <Box>
                <Text fontSize="md" color="gray.700" mb={4}>
                  Upload your existing contact list and our AI will
                  automatically format it for you. No need to worry about column
                  headers or formatting - we'll handle it all!
                </Text>

                <List spacing={2}>
                  <ListItem>
                    <HStack align="start">
                      <ListIcon as={CheckCircleIcon} color="green.500" mt={1} />
                      <Text fontSize="sm" color="gray.600">
                        Automatically detects names, emails, and phone numbers
                      </Text>
                    </HStack>
                  </ListItem>
                  <ListItem>
                    <HStack align="start">
                      <ListIcon as={CheckCircleIcon} color="green.500" mt={1} />
                      <Text fontSize="sm" color="gray.600">
                        Intelligently formats phone numbers
                      </Text>
                    </HStack>
                  </ListItem>
                  <ListItem>
                    <HStack align="start">
                      <ListIcon as={CheckCircleIcon} color="green.500" mt={1} />
                      <Text fontSize="sm" color="gray.600">
                        Removes duplicates automatically
                      </Text>
                    </HStack>
                  </ListItem>
                </List>
              </Box>

              {/* File Uploader */}
              <Box>
                <Text fontWeight="600" mb={3} fontSize="sm" color="gray.600">
                  Select CSV File
                </Text>
                <FileUploader
                  handleChange={handleFileChange}
                  name="file"
                  types={fileTypes}
                  multiple={false}
                  label="Upload or drop your CSV file here"
                  hoverTitle=" "
                  maxSize={10}
                />
                {selectedFile && (
                  <Box mt={3} p={3} bg="blue.50" borderRadius="md">
                    <HStack>
                      <Icon as={CheckCircleIcon} color="blue.500" />
                      <Text fontSize="sm" color="blue.700" fontWeight="500">
                        {selectedFile.name}
                      </Text>
                    </HStack>
                  </Box>
                )}
              </Box>

              {/* Upload Button */}
              <HStack spacing={3} pt={2}>
                <Button
                  flex={1}
                  variant="outline"
                  onClick={handleClose}
                  isDisabled={isUploading}
                >
                  Cancel
                </Button>
                <Button
                  flex={1}
                  colorScheme="blue"
                  leftIcon={<ArrowUpIcon />}
                  onClick={handleUpload}
                  isLoading={isUploading}
                  loadingText="Uploading..."
                  isDisabled={!selectedFile}
                >
                  Upload
                </Button>
              </HStack>
            </VStack>
          )}

          {/* Processing Success State */}
          {modalState === "processing" && (
            <VStack spacing={6} align="stretch" py={4}>
              <Box display="flex" justifyContent="center">
                <Box
                  width="80px"
                  height="80px"
                  borderRadius="full"
                  bg="green.500"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 8px 20px rgba(72, 187, 120, 0.3)"
                >
                  <Icon as={CheckCircleIcon} boxSize={12} color="white" />
                </Box>
              </Box>

              <Box textAlign="center">
                <Heading size="md" color="gray.800" mb={2}>
                  Upload Successful!
                </Heading>
                <Text fontSize="md" color="gray.600" mb={4}>
                  We're processing your contacts in the background. This may
                  take some time depending on the size of your list. We will
                  send you an email when it's done!
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Refresh the page or check back in a few moments to see your
                  updated contact list.
                </Text>
              </Box>

              <Button colorScheme="blue" onClick={handleDone}>
                Got it
              </Button>
            </VStack>
          )}

          {/* Error State */}
          {modalState === "error" && (
            <VStack spacing={6} align="stretch" py={4}>
              <Box display="flex" justifyContent="center">
                <Box
                  width="80px"
                  height="80px"
                  borderRadius="full"
                  bg="red.500"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 8px 20px rgba(245, 101, 101, 0.3)"
                >
                  <Icon as={WarningIcon} boxSize={10} color="white" />
                </Box>
              </Box>

              <Box textAlign="center">
                <Heading size="md" color="gray.800" mb={2}>
                  Upload Failed
                </Heading>
                <Text fontSize="md" color="gray.600" mb={4}>
                  We encountered an error while uploading your contacts.
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Please contact us at{" "}
                  <Text as="span" color="blue.500" fontWeight="600">
                    hello@skipgive.com
                  </Text>{" "}
                  for assistance.
                </Text>
              </Box>

              <HStack spacing={3}>
                <Button flex={1} variant="outline" onClick={handleClose}>
                  Close
                </Button>
                <Button
                  flex={1}
                  colorScheme="blue"
                  onClick={() => setModalState("upload")}
                >
                  Try Again
                </Button>
              </HStack>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
